import { retry } from '../async/retry.js'
import { Logger } from '../logger/index.js'
import { DurationLike } from '../time/duration/UnixDuration.js'
import { HttpError, methodSchema } from './errors.js'

export interface RetryOptions {
  fetch: FetchType
  maxAttempts: number
  delay: DurationLike | ((attempt: number) => DurationLike)
  isRetryableStatus: (status: number) => boolean
  logger: Logger
}

type FetchType = typeof fetch

export function fetchRetry(options: RetryOptions): FetchType {
  return (url, requestInit = {}) => {
    return retry({
      fn: async () => {
        const result = await options.fetch(url, requestInit)

        if (!result.ok) {
          const method = (requestInit.method ?? 'get').toUpperCase()
          throw new HttpError(methodSchema.parse(method), result.url, result.status, await result.text())
        }

        return result
      },
      shouldRetry(error) {
        if (error instanceof HttpError) {
          return options.isRetryableStatus(error.status)
        }
        return true // most likely networking error -- refetch
      },
      delay: options.delay,
      maxAttempts: options.maxAttempts,
      logger: options.logger,
    })
  }
}

export const defaultRetryOptions: RetryOptions = {
  fetch: (...args) => fetch(...args), // @note: passing fetch directly would lose binding to window in browser
  maxAttempts: 5,
  delay: retry.exponentialBackoff({ milliseconds: 150 }),
  isRetryableStatus: (status) => status >= 500,
  logger: Logger.SILENT,
}
